import { hardNavigation } from "../../../utils/hard-navigation.util";
import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";

/**
 * CrossAppAuthService handles cross-domain authentication flows
 * between the main app, admin app, and CMC app.
 *
 * This service provides methods to:
 * - Redirect unauthenticated users to the main app for login
 * - Redirect authenticated users back to external apps with tokens
 * - Validate redirect URLs against allowed domains
 *
 * Domain patterns supported:
 * - Production: app.vengreso.com, admin.vengreso.com, cmc.vengreso.com
 * - Staging: staging-app.vengreso.com, staging-admin.vengreso.com, staging-cmc.vengreso.com
 * - Local: localhost:4200, localhost:4201, localhost:4202
 */
@Injectable({
  providedIn: "root",
})
export class CrossAppAuthService {
  /**
   * Gets the current protocol (http or https) based on the window location
   */
  private get currentProtocol(): string {
    return window.location.protocol;
  }

  /**
   * Determines if the current app is the main app (app.vengreso.com or staging-app.vengreso.com)
   * or a split app (admin/cmc)
   */
  get isMainApp(): boolean {
    const currentHost = window.location.host;

    // Check environment config first
    const mainAppUrl = (environment as any).MAIN_APP_URL;
    if (mainAppUrl) {
      try {
        const mainHost = new URL(mainAppUrl).host;
        if (currentHost === mainHost) return true;
      } catch {
        // Continue to pattern matching
      }
    }

    // Pattern matching for main app domains
    // Matches: app.vengreso.com, staging-app.vengreso.com, localhost:4200
    return (
      currentHost.includes("app.vengreso.com") ||
      currentHost === "localhost:4200" ||
      currentHost === "localhost" ||
      currentHost === "192.168.1.50:8080" ||
      currentHost === "192.168.1.50"
    );
  }

  /**
   * Determines if the current app is the Admin app
   */
  get isAdminApp(): boolean {
    const currentHost = window.location.host;

    // Check environment config first
    const adminAppUrl = (environment as any).ADMIN_APP_URL;
    if (adminAppUrl) {
      try {
        const adminHost = new URL(adminAppUrl).host;
        if (currentHost === adminHost) return true;
      } catch {
        // Continue to pattern matching
      }
    }

    // Pattern matching for admin app domains
    // Matches: admin.vengreso.com, staging-admin.vengreso.com, localhost:4201
    return (
      currentHost.includes("admin.vengreso.com") ||
      currentHost === "localhost:4201"
    );
  }

  /**
   * Determines if the current app is the CMC app
   */
  get isCmcApp(): boolean {
    const currentHost = window.location.host;

    // Check environment config first
    const cmcAppUrl = (environment as any).CMC_APP_URL;
    if (cmcAppUrl) {
      try {
        const cmcHost = new URL(cmcAppUrl).host;
        if (currentHost === cmcHost) return true;
      } catch {
        // Continue to pattern matching
      }
    }

    // Pattern matching for CMC app domains
    // Matches: cmc.vengreso.com, staging-cmc.vengreso.com, localhost:4202
    return (
      currentHost.includes("cmc.vengreso.com") ||
      currentHost === "localhost:4202"
    );
  }

  /**
   * Gets the main app URL from environment config or derives it from current host
   */
  get mainAppUrl(): string {
    const envUrl = (environment as any).MAIN_APP_URL;
    if (envUrl) return envUrl;

    return this.deriveAppUrl("app");
  }

  /**
   * Gets the admin app URL from environment config or derives it from current host
   */
  get adminAppUrl(): string {
    const envUrl = (environment as any).ADMIN_APP_URL;
    if (envUrl) return envUrl;

    return this.deriveAppUrl("admin");
  }

  /**
   * Gets the CMC app URL from environment config or derives it from current host
   */
  get cmcAppUrl(): string {
    const envUrl = (environment as any).CMC_APP_URL;
    if (envUrl) return envUrl;

    return this.deriveAppUrl("cmc");
  }

  /**
   * Gets the Admin app domain from environment config or derives it from current host (root URL)
   */
  get adminAppDomain(): string {
    const envDomain = (environment as any).ADMIN_APP_DOMAIN;
    if (envDomain) return envDomain;

    return this.deriveAppUrl("admin");
  }

  /**
   * Gets the CMC app domain from environment config or derives it from current host (root URL)
   */
  get cmcAppDomain(): string {
    const envDomain = (environment as any).CMC_APP_DOMAIN;
    if (envDomain) return envDomain;

    return this.deriveAppUrl("cmc");
  }

  /**
   * Derives the app URL based on the current hostname pattern
   * Supports: production, staging, and local development
   * @param appType - The type of app: 'app', 'admin', or 'cmc'
   */
  private deriveAppUrl(appType: "app" | "admin" | "cmc"): string {
    const currentHost = window.location.host;
    const protocol = this.currentProtocol;

    // Local development
    if (currentHost.startsWith("localhost")) {
      const portMap = { app: "4200", admin: "4201", cmc: "4202" };
      return `${protocol}//localhost:${portMap[appType]}`;
    }

    // Staging pattern: staging-app.vengreso.com -> staging-admin.vengreso.com
    if (currentHost.includes("staging-")) {
      return `${protocol}//staging-${appType}.vengreso.com`;
    }

    // Production pattern: app.vengreso.com -> admin.vengreso.com
    return `${protocol}//${appType}.vengreso.com`;
  }

  /**
   * Redirects to the main app for authentication validation.
   * The main app will check if the user is logged in and redirect back with tokens.
   *
   * @param currentPath - The current path to return to after authentication
   */
  redirectToMainAppForAuth(currentPath?: string): void {
    const currentUrl = window.location.host;
    const path = currentPath || window.location.pathname;
    const encodedRedirect = encodeURIComponent(currentUrl + "/session/auth/callback");
    const encodedPath = encodeURIComponent(path);

    hardNavigation.navigate(`${this.mainAppUrl}/session/auth/validate?redirect=${encodedRedirect}&path=${encodedPath}`);
  }

  /**
   * Redirects to the main app login page
   */
  redirectToMainAppLogin(): void {
    hardNavigation.navigate(`${this.mainAppUrl}/session/signin`);
  }

  /**
   * Redirects to the main app flyboard (user dashboard)
   */
  redirectToMainAppFlyboard(): void {
    hardNavigation.navigate(`${this.mainAppUrl}/flyboard`);
  }

  /**
   * Redirects to the admin app with optional company slug
   * Supports a redirect query parameter for specific redirect URLs
   * @param companySlug - The company slug for admin routes
   * @param redirectUrl - Optional specific URL to redirect to (from query param)
   */
  redirectToAdminApp(companySlug?: string, redirectUrl?: string): void {
    // If a specific redirect URL is provided and it's valid, use it
    if (redirectUrl && this.isAllowedAdminRedirect(redirectUrl)) {
      hardNavigation.navigate(redirectUrl);
      return;
    }

    // Otherwise redirect to the admin app domain root (ADMIN_APP_DOMAIN)
    hardNavigation.navigate(this.adminAppDomain);
  }

  /**
   * Redirects to the CMC app
   * Supports a redirect query parameter for specific redirect URLs
   * @param redirectUrl - Optional specific URL to redirect to (from query param)
   */
  redirectToCmcApp(redirectUrl?: string): void {
    // If a specific redirect URL is provided and it's valid, use it
    if (redirectUrl && this.isAllowedCmcRedirect(redirectUrl)) {
      hardNavigation.navigate(redirectUrl);
      return;
    }

    // Otherwise redirect to the CMC app domain root (CMC_APP_DOMAIN)
    hardNavigation.navigate(this.cmcAppDomain);
  }

  /**
   * Validates that a redirect URL is allowed for Admin app
   * @param url - The URL to validate
   */
  isAllowedAdminRedirect(url: string): boolean {
    const adminDomain = (environment as any).ADMIN_APP_DOMAIN;
    if (!adminDomain) return false;

    try {
      const urlHost = new URL(url).host;
      const adminHost = new URL(adminDomain).host;
      return urlHost === adminHost;
    } catch {
      return false;
    }
  }

  /**
   * Validates that a redirect URL is allowed for CMC app
   * @param url - The URL to validate
   */
  isAllowedCmcRedirect(url: string): boolean {
    const cmcDomain = (environment as any).CMC_APP_DOMAIN;
    if (!cmcDomain) return false;

    try {
      const urlHost = new URL(url).host;
      const cmcHost = new URL(cmcDomain).host;
      return urlHost === cmcHost;
    } catch {
      return false;
    }
  }

  /**
   * Validates that a redirect URL is one of the allowed external domains
   * @param url - The URL to validate
   */
  isAllowedRedirect(url: string): boolean {
    const allowedDomains = this.getAllowedDomains();
    return allowedDomains.some(
      (domain) => url.includes(domain)
    );
  }

  /**
   * Gets the list of allowed redirect domains
   */
  private getAllowedDomains(): string[] {
    const domains: string[] = [];

    // Add configured domains from environment
    const adminDomain = (environment as any).ADMIN_APP_DOMAIN;
    const cmcDomain = (environment as any).CMC_APP_DOMAIN;

    if (adminDomain) {
      domains.push(adminDomain);
    }
    if (cmcDomain) {
      domains.push(cmcDomain);
    }

    // Fallback domains for production
    domains.push("admin.vengreso.com", "cmc.vengreso.com");

    // Staging domains
    domains.push("staging-admin.vengreso.com", "staging-cmc.vengreso.com");

    // Add localhost for development
    if (!environment.production) {
      domains.push("localhost:4201", "localhost:4202", "localhost", "192.168.1.50:8081", "192.168.1.50:8082", "192.168.1.50");
    }

    return domains;
  }

  /**
   * Builds a redirect URL with tokens for cross-domain authentication
   * @param redirectDomain - The domain/URL to redirect to
   * @param accessToken - The access token
   * @param refreshToken - The refresh token
   * @param targetPath - The target path after authentication
   */
  buildRedirectWithTokens(
    redirectDomain: string,
    accessToken: string,
    refreshToken: string,
    targetPath: string = "/"
  ): string {
    const separator = redirectDomain.includes("?") ? "&" : "?";
    const encodedPath = encodeURIComponent(targetPath);

    // If redirectDomain already has a protocol, use it as-is
    if (redirectDomain.startsWith("http://") || redirectDomain.startsWith("https://")) {
      return `${redirectDomain}${separator}token=${accessToken}&refresh=${refreshToken}&path=${encodedPath}`;
    }

    // Otherwise, use the current protocol
    const protocol = this.currentProtocol;
    return `${protocol}//${redirectDomain}${separator}token=${accessToken}&refresh=${refreshToken}&path=${encodedPath}`;
  }

  /**
   * Performs logout and redirects to main app login
   * Used by admin/CMC apps when logging out
   */
  logoutAndRedirectToMainApp(): void {
    // Clear local storage
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userDetails");

    // Redirect to main app login
    this.redirectToMainAppLogin();
  }
}
